import React, { useState, useRef } from 'react';
import { JsonRpc } from '@proton/js';
import { Plus, X } from 'lucide-react';
import FilterDropdown from './FilterDropdown';
import DateTimePicker from './DateTimePicker';
import ImageUpload from './ImageUpload';
import {
  MAX_MULTI_OUTCOMES,
  max_description_characters,
  max_resolution_characters,
} from '../configs/config';
import { createMarketProposalTransaction } from '../utils/crypto';

interface AddProposalProps {
  session: any;
  onSuccess: () => void;
}

const MAX_OUTCOME_LENGTH = 64;
const OPTION_NAME_REGEX = /^[a-zA-Z0-9 _.()/'&-]+$/;
const CATEGORY_OPTIONS = [
  { value: 'Crypto', label: 'Crypto' },
  { value: 'Politics', label: 'Politics' },
  { value: 'Sports', label: 'Sports' },
  { value: 'Technology', label: 'Technology' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Other', label: 'Other' },
];
const CATEGORY_PLACEHOLDER_OPTION = { value: '', label: 'Select category' };

const validateOutcomeName = (name: string): string | null => {
  if (name.includes(',')) {
    return 'Commas are not allowed in option names';
  }
  if (name.length > MAX_OUTCOME_LENGTH) {
    return `Option name must be ${MAX_OUTCOME_LENGTH} characters or fewer`;
  }
  if (name.trim() !== '' && !OPTION_NAME_REGEX.test(name)) {
    return 'Special characters are not allowed in option names';
  }
  return null;
};

const AddProposal: React.FC<AddProposalProps> = ({ session, onSuccess }) => {
  const [question, setQuestion] = useState('');
  const [category, setCategory] = useState('');
  const [expireDate, setExpireDate] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [uploadingImage, setUploadingImage] = useState(false);
  const [marketType, setMarketType] = useState<'binary' | 'multi'>('binary');
  const [outcomes, setOutcomes] = useState<string[]>(['Yes', 'No']);
  const [createLoading, setCreateLoading] = useState(false);
  const [description, setDescription] = useState('');
  const [resolutionCriteria, setResolutionCriteria] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const formRef = useRef<HTMLFormElement>(null);

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleImageFileSelect = (file: File) => {
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      showToast('Only JPG, PNG, and WebP images are allowed', 'error');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      showToast('Image must be less than 2MB', 'error');
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleUploadImage = async () => {
    if (!imageFile) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', imageFile);

      const response = await fetch('/api/upload.php', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      setImageUrl(data.url);
      showToast('Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      showToast('Failed to upload image: ' + error, 'error');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleClearImage = () => {
    setImageFile(null);
    setImagePreview('');
    setImageUrl('');
  };

  const handleAddOutcome = () => {
    if (outcomes.length >= MAX_MULTI_OUTCOMES) {
      showToast(`Maximum ${MAX_MULTI_OUTCOMES} outcomes allowed`, 'error');
      return;
    }
    setOutcomes([...outcomes, '']);
  };

  const handleRemoveOutcome = (index: number) => {
    if (outcomes.length <= 2) {
      showToast('Minimum 2 outcomes required', 'error');
      return;
    }
    const nextOutcomes = outcomes.filter((_, i) => i !== index);

    setOutcomes(nextOutcomes);
    if (nextOutcomes.length < 3) {
      setMarketType('binary');
    }
  };

  const handleOutcomeChange = (index: number, value: string) => {
    const nextValue = value.replace(/^\s+/, '');

    if (nextValue.includes(',')) {
      showToast('Commas are not allowed in option names', 'error');
      return;
    }
    if (nextValue.length > MAX_OUTCOME_LENGTH) {
      showToast(`Option name must be ${MAX_OUTCOME_LENGTH} characters or fewer`, 'error');
      return;
    }
    if (nextValue.trim() !== '' && !OPTION_NAME_REGEX.test(nextValue)) {
      showToast('Special characters are not allowed in option names', 'error');
      return;
    }
    const newOutcomes = [...outcomes];
    newOutcomes[index] = nextValue;
    setOutcomes(newOutcomes);
  };

  const handleMarketTypeChange = (type: 'binary' | 'multi') => {
    setMarketType(type);
    if (type === 'binary') {
      setOutcomes(['Yes', 'No']);
    } else {
      setOutcomes(['Option 1', 'Option 2', 'Option 3']);
    }
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value.slice(0, max_description_characters));
  };

  const handleResolutionCriteriaChange = (value: string) => {
    setResolutionCriteria(value.slice(0, max_resolution_characters));
  };

  const handleShowPreview = (e: React.FormEvent) => {
    e.preventDefault();
    if (!session || !question || !category || !expireDate) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    const validOutcomes = outcomes.filter(o => o.trim() !== '');
    if (validOutcomes.length < 2) {
      showToast('At least 2 outcomes are required', 'error');
      return;
    }
    if (validOutcomes.length > MAX_MULTI_OUTCOMES) {
      showToast(`Maximum ${MAX_MULTI_OUTCOMES} outcomes allowed`, 'error');
      return;
    }
    for (const name of validOutcomes) {
      const error = validateOutcomeName(name);
      if (error) {
        showToast(error, 'error');
        return;
      }
    }

    if (!resolutionCriteria.trim()) {
      showToast('Resolution criteria is required', 'error');
      return;
    }

    setShowPreview(true);
  };

  const resetForm= () => {
    setQuestion('');
    setCategory('');
    setExpireDate('');
    setImageUrl('');
    setImageFile(null);
    setImagePreview('');
    setMarketType('binary');
    setOutcomes(['Yes', 'No']);
    setDescription('');
    setResolutionCriteria('');
    setShowPreview(false);
  };

  const handleCreateMarket = async () => {
    if (!session || !question || !category || !expireDate) {
      showToast('Please fill in all fields', 'error');
      return;
    }

    const validOutcomes = outcomes.filter(o => o.trim() !== '');
    if (validOutcomes.length < 2) {
      showToast('At least 2 outcomes are required', 'error');
      return;
    }
    if (validOutcomes.length > MAX_MULTI_OUTCOMES) {
      showToast(`Maximum ${MAX_MULTI_OUTCOMES} outcomes allowed`, 'error');
      return;
    }
    for (const name of validOutcomes) {
      const error = validateOutcomeName(name);
      if (error) {
        showToast(error, 'error');
        return;
      }
    }

    if (!resolutionCriteria.trim()) {
      showToast('Resolution criteria is required', 'error');
      return;
    }

    setCreateLoading(true);
    try {
      const rpc = new JsonRpc(process.env.REACT_APP_RPC_ENDPOINT || process.env.REACT_APP_PROTON_ENDPOINT || 'https://proton.eosusa.io');
      const contractName = process.env.REACT_APP_CONTRACT_NAME || 'xpredict';
      const expireTimestamp = Math.floor(new Date(expireDate).getTime() / 1000);

      // Capture max market_id BEFORE creating the market to avoid race conditions
      let maxMarketIdBefore = 0;
      try {
        const beforeResult = await rpc.get_table_rows({
          json: true,
          code: contractName,
          scope: contractName,
          table: 'markets3',
          limit: 1,
          reverse: true,
        });
        if (beforeResult.rows.length > 0) {
          maxMarketIdBefore = beforeResult.rows[0].id;
        }
      } catch (e) {
        console.error('Error fetching max market_id before creation:', e);
      }

      await createMarketProposalTransaction(
        session,
        marketType,
        {
          admin: session.auth.actor.toString(),
          question: question,
          category: category,
          expireTime: expireTimestamp,
          image_url: imageUrl,
          outcomes: validOutcomes,
        },
      );

      // After market creation, find the new market by matching question + category + expireTime
      try {
        let afterResult = await rpc.get_table_rows({
          json: true,
          code: contractName,
          scope: contractName,
          table: 'markets3',
          lower_bound: maxMarketIdBefore + 1,
          limit: 10,
        });

        // Blockchain state may not reflect the new market immediately; retry once after a short delay
        if (afterResult.rows.length === 0) {
          await new Promise(resolve => setTimeout(resolve, 3000));
          afterResult = await rpc.get_table_rows({
            json: true,
            code: contractName,
            scope: contractName,
            table: 'markets3',
            lower_bound: maxMarketIdBefore + 1,
            limit: 10,
          });
        }

        let newMarketId = 0;
        for (const market of afterResult.rows) {
          if (market.question === question &&
              market.category === category &&
              market.expireTime === expireTimestamp) {
            newMarketId = market.id;
            break;
          }
        }

        // Fallback: if no match found, use the latest market with id > maxMarketIdBefore
        if (newMarketId === 0 && afterResult.rows.length > 0) {
          newMarketId = afterResult.rows[afterResult.rows.length - 1].id;
        }

        if (newMarketId > 0) {
          await fetch('/api/market_meta.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              market_id: newMarketId,
              description: description,
              resolution_criteria: resolutionCriteria,
            }),
          });
        }
      } catch (metaError) {
        console.error('Error saving market metadata:', metaError);
      }

      showToast('Market proposal submitted successfully!');
      resetForm();

      // Redirect to My Prediction Proposals after a short delay
      setTimeout(() => {
        onSuccess();
      }, 1500);
    } catch (error) {
      console.error('Error creating market:', error);
      showToast('Failed to create market: ' + error, 'error');
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <div className="admin-panel">
      <h2>Add Market Proposal</h2>
      <p className="admin-subtitle">
        Submit a new prediction market proposal. After submission, your proposal will need community approval before going live.
      </p>

      {!showPreview ? (
        <div className="admin-section">
          <h3>Create New Market</h3>
          <form ref={formRef} onSubmit={handleShowPreview} className="admin-form">
            <div className="form-group">
              <label>Question</label>
              <input
                type="text"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Will Bitcoin reach $100,000 by end of 2025?"
                required
              />
            </div>

            <div className="form-group">
              <label>Category</label>
              <FilterDropdown
                value={category}
                options={
                  category
                    ? CATEGORY_OPTIONS
                    : [CATEGORY_PLACEHOLDER_OPTION, ...CATEGORY_OPTIONS]
                }
                onChange={setCategory}
                ariaLabel="Select market category"
              />
            </div>

            <div className="form-group">
              <label>Expiration Date</label>
              <DateTimePicker
                value={expireDate}
                onChange={setExpireDate}
                ariaLabel="Select expiration date and time"
                placeholder="Select expiration date and time"
              />
            </div>

            <div className="form-group">
              <label>Market Type</label>
              <div className="button-group">
                <button
                  type="button"
                  className={marketType === 'binary' ? 'active' : ''}
                  onClick={() => handleMarketTypeChange('binary')}
                >
                  Binary (Yes/No)
                </button>
                <button
                  type="button"
                  className={marketType === 'multi' ? 'active' : ''}
                  onClick={() => handleMarketTypeChange('multi')}
                >
                  Multi-Outcome
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>Outcomes</label>
              <div className="outcomes-list proposal-outcomes-list">
                {outcomes.map((outcome, index) => (
                  <div key={index} className="outcome-item proposal-outcome-item">
                    <input
                      type="text"
                      value={outcome}
                      onChange={(e) => handleOutcomeChange(index, e.target.value)}
                      placeholder={`Outcome ${index + 1}`}
                      required
                    />
                    {marketType === 'multi' && outcomes.length > 2 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveOutcome(index)}
                        className="remove-outcome-button"
                        aria-label={`Remove outcome ${index + 1}`}
                      >
                        <X />
                      </button>
                    )}
                  </div>
                ))}
                {marketType === 'multi' && outcomes.length < MAX_MULTI_OUTCOMES && (
                  <button
                    type="button"
                    onClick={handleAddOutcome}
                    className="add-outcome-button"
                    aria-label="Add outcome"
                  >
                    <Plus />
                    <span>Add Outcome</span>
                  </button>
                )}
              </div>
            </div>

            <div className="form-group">
              <label>Description (optional)</label>
              <textarea
                value={description}
                onChange={(e) => handleDescriptionChange(e.target.value)}
                placeholder="Provide additional context or details about this market..."
                rows={3}
                maxLength={max_description_characters}
                className="description-textarea"
              />
              <div className="textarea-meta">
                <span
                  className={`textarea-counter ${description.length >= max_description_characters ? 'limit-reached' : ''}`}
                >
                  {description.length}/{max_description_characters}
                </span>
              </div>
              {description.length >= max_description_characters && (
                <p className="textarea-limit-message">
                  Description limit is {max_description_characters} characters.
                </p>
              )}
            </div>

            <div className="form-group">
              <label>Resolution Criteria <span className="required-indicator">*</span></label>
              <textarea
                value={resolutionCriteria}
                onChange={(e) => handleResolutionCriteriaChange(e.target.value)}
                placeholder="Please enter very detailed Rules, Dates, and a Description on how the result is determined when the market is resolved."
                rows={4}
                maxLength={max_resolution_characters}
                className="resolution-textarea"
                required
              />
              <div className="textarea-meta">
                <span
                  className={`textarea-counter ${resolutionCriteria.length >= max_resolution_characters ? 'limit-reached' : ''}`}
                >
                  {resolutionCriteria.length}/{max_resolution_characters}
                </span>
              </div>
              {resolutionCriteria.length >= max_resolution_characters && (
                <p className="textarea-limit-message">
                  Resolution criteria limit is {max_resolution_characters} characters.
                </p>
              )}
            </div>

            <div className="form-group">
              <label>Market Image (Optional)</label>
              <ImageUpload
                imageUrl={imageUrl}
                imagePreview={imagePreview}
                imageFile={imageFile}
                uploading={uploadingImage}
                onImageUrlChange={setImageUrl}
                onFileSelect={handleImageFileSelect}
                onUpload={handleUploadImage}
                onClear={handleClearImage}
              />
            </div>

            <button
              type="submit"
              disabled={createLoading}
              className="submit-button"
            >
              Preview & Confirm
            </button>
          </form>
        </div>
      ) : (
        <div className="admin-section">
          <h3>Review Your Market Proposal</h3>
          <div className="preview-content">
            <div className="preview-section">
              <h4>Question</h4>
              <p className="preview-question">{question}</p>
            </div>

            <div className="preview-row">
              <div className="preview-section">
                <h4>Category</h4>
                <p>{category}</p>
              </div>
              <div className="preview-section">
                <h4>Expiration</h4>
                <p>{expireDate ? new Date(expireDate).toLocaleString() : 'Not set'}</p>
              </div>
            </div>

            <div className="preview-section">
              <h4>Outcomes</h4>
              <ul className="preview-outcomes">
                {outcomes.filter(o => o.trim() !== '').map((outcome, index) => (
                  <li key={index}>{outcome}</li>
                ))}
              </ul>
            </div>

            {description && (
              <div className="preview-section">
                <h4>Description</h4>
                <p className="preview-description">{description}</p>
              </div>
            )}

            {resolutionCriteria && (
              <div className="preview-section">
                <h4>Resolution Criteria</h4>
                <p className="preview-resolution">{resolutionCriteria}</p>
              </div>
            )}

            {(imageUrl || imagePreview) && (
              <div className="preview-section">
                <h4>Market Image</h4>
                <div className="preview-image">
                  <img src={imagePreview || imageUrl} alt="Market preview" />
                </div>
              </div>
            )}

            <div className="preview-warning">
              <strong>Important:</strong> Please review all details carefully before submitting your market proposal.
            </div>
          </div>

          <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
            <button
              className="cancel-button"
              onClick={() => setShowPreview(false)}
              disabled={createLoading}
            >
              Go Back & Edit
            </button>
            <button
              className="confirm-button"
              onClick={handleCreateMarket}
              disabled={createLoading}
            >
              {createLoading ? 'Submitting Proposal...' : 'Confirm & Submit Proposal'}
            </button>
          </div>
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div className={`toast-notification ${toast.type}`}>
          <span className="toast-message">{toast.message}</span>
          <button className="toast-close" onClick={() => setToast(null)}>&times;</button>
        </div>
      )}
    </div>
  );
};

export default AddProposal;
